import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { profileApi } from '@entities/profile';
import { Skeleton, UiText } from '@shared/ui';

import cls from './Profile.module.css';
import { ProfileEditForm, ProfileSummary } from './ui';
import { ProfileCard } from './ui/ProfileCard';

type ProfilePageProps = {
    onPaySubscriptionClick?: ({ removeProlong }: { removeProlong: boolean }) => void;
};

export const ProfilePage = ({ onPaySubscriptionClick }: ProfilePageProps) => {
    const { t } = useTranslation();
    const { data: profile, isPending, isError } = useQuery(profileApi.getProfileQueryOptions());

    if (isPending) {
        return (
            <main className={cls.profilePage} aria-busy="true">
                <ProfileCard className={cls.profileSummarySkeleton}>
                    <Skeleton width={160} height={24} border="6px" classes={cls.summaryTitle} />
                    <div className={cls.profileDetailsSkeleton}>
                        <Skeleton width={90} height={90} border="50%" />
                        <Skeleton width={180} height={20} border="6px" />
                        <Skeleton width={220} height={16} border="6px" />
                        <Skeleton width={170} height={16} border="6px" />
                        <Skeleton width={130} height={14} border="6px" />
                    </div>
                    <Skeleton width="100%" height={36} border="6px" />
                </ProfileCard>
                <ProfileCard className={cls.profileEditFormSkeleton}>
                    <div className={cls.editHeaderSkeleton}>
                        <Skeleton width={180} height={24} border="6px" />
                        <div className={cls.tabsSkeleton}>
                            <Skeleton width={70} height={32} border="6px" />
                            <Skeleton width={70} height={32} border="6px" />
                            <Skeleton width={90} height={32} border="6px" />
                            <Skeleton width={64} height={32} border="6px" />
                        </div>
                    </div>
                    <div className={cls.formFieldsSkeleton}>
                        <Skeleton height={40} border="6px" />
                        <Skeleton height={40} border="6px" />
                        <Skeleton height={40} border="6px" />
                        <Skeleton width={180} height={36} border="6px" />
                    </div>
                </ProfileCard>
            </main>
        );
    }

    if (isError || !profile) {
        return (
            <main className={cls.profileState}>
                <UiText role="alert">{t('profile.page.errors.load')}</UiText>
            </main>
        );
    }

    return (
        <main className={cls.profilePage}>
            <ProfileSummary profile={profile} onPaySubscriptionClick={onPaySubscriptionClick} />
            <ProfileEditForm profile={profile} onPaySubscriptionClick={onPaySubscriptionClick} />
        </main>
    );
};
